// When making changes to this file, please make sure to test routes and ensure performance does not regress.
// Changes to this file have a wide impact on the app and across multiple teams.
import React, {useMemo} from 'react';
import {Redirect, Route, Switch} from 'react-router';

import {IFrameAccessDenied} from '../components/AccessDenied';
import NoMatch from '../components/NoMatch';
import {auth} from '../setup';
import {
  hasShellErrorHandler,
  isMountedInShell,
  reportUnauthenticatedToShell,
} from '../shellRemote';
import {loginPage} from '../util/urls';
import {isInIframe} from '../util/window';
import {makeRoutes} from './makeRoutes';
import {getRouteTab, TrackRouteComponent} from './TrackRouteComponent';

// When mounted in the Forge shell, a private route cannot send the user to the
// app's own /login: the shell owns the browser location and identity lives on a
// different origin. Report through the shell's error channel instead and let it
// decide where to go. Reporting from an effect keeps this cross-root call off the
// render path, so it fires once per mount rather than on every re-render.
const ShellUnauthenticated = () => {
  React.useEffect(() => {
    reportUnauthenticatedToShell();
  }, []);

  return null;
};

export const AppRoutes = React.memo(() => {
  const filteredRoutes = useMemo(
    () =>
      makeRoutes().filter(route =>
        'condition' in route ? route.condition !== false : true
      ),
    []
  );

  return (
    <Switch>
      {filteredRoutes.map(route => {
        // Do not use the index as the key because this is NOT a static list of routes.
        // These are filtered down based on `condition`
        const key = Array.isArray(route.path)
          ? route.path.join('|')
          : route.path;

        if ('redirect' in route) {
          const {redirect} = route;
          if (typeof redirect === 'function') {
            return (
              <Route
                key={key}
                path={route.path}
                render={({location}) => <Redirect to={redirect(location)} />}
              />
            );
          }
          return <Redirect key={key} from={route.path} to={redirect} />;
        }

        const {RouteComponent} = route;

        return (
          <Route
            key={key}
            exact={route.exact}
            path={route.path}
            render={routeRenderProps => {
              const {additionalProps} = route;

              // Auth check must happen first, before skipTracking or rendering
              if ('isPrivateRoute' in route && route.isPrivateRoute === true) {
                if (!auth.loggedIn()) {
                  if (isMountedInShell() && hasShellErrorHandler()) {
                    return <ShellUnauthenticated />;
                  }
                  return (
                    <Redirect
                      to={{
                        pathname: loginPage(),
                        state: {from: routeRenderProps.location},
                      }}
                    />
                  );
                }
              }

              const shouldBlockIframe =
                isInIframe() && !additionalProps.allowIframes;

              // Route handles its own tracking internally (or intentionally skips it),
              // but iframe access rules should still apply consistently.
              if ('skipTracking' in additionalProps) {
                return shouldBlockIframe ? (
                  <IFrameAccessDenied />
                ) : (
                  <RouteComponent />
                );
              }

              const tab = getRouteTab(routeRenderProps.match, additionalProps);

              // Private route (auth already checked above)
              if ('isPrivateRoute' in route && route.isPrivateRoute === true) {
                return (
                  <TrackRouteComponent
                    datadogViewName={additionalProps.datadogViewName}
                    tabParam={tab}>
                    {shouldBlockIframe ? (
                      <IFrameAccessDenied />
                    ) : (
                      <RouteComponent
                        additionalProps={additionalProps}
                        allowAnonymous={false}
                        allowIframes={additionalProps.allowIframes}
                        match={routeRenderProps.match}
                        datadogViewName={additionalProps.datadogViewName}
                      />
                    )}
                  </TrackRouteComponent>
                );
              }

              // Public route
              if ('isPrivateRoute' in route && route.isPrivateRoute === false) {
                return (
                  <TrackRouteComponent
                    datadogViewName={additionalProps.datadogViewName}
                    tabParam={tab}>
                    {shouldBlockIframe ? (
                      <IFrameAccessDenied />
                    ) : (
                      <RouteComponent
                        tab={additionalProps.tab}
                        allowAnonymous={true}
                        allowIframes={additionalProps.allowIframes}
                        match={routeRenderProps.match}
                        datadogViewName={additionalProps.datadogViewName}
                      />
                    )}
                  </TrackRouteComponent>
                );
              }

              // This should never happen. If it does, it's a bug.
              console.error('Invalid route configuration:', route);
              return null;
            }}
          />
        );
      })}
      {/* Catch-all route: Rendered when no other routes match (404 page) */}
      <TrackRouteComponent datadogViewName="NoMatch">
        <NoMatch />
      </TrackRouteComponent>
    </Switch>
  );
});
